// ============================================
// Path: /frontend/src/components/admin/ListingsTable.jsx
// Purpose: Admin table for managing listings with approve/reject actions
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listingsAPI } from '../../api/listings.api';
import { useToast } from '../../hooks/useToast';
import { CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Pagination } from '../ui/Pagination';
import { Eye, CheckCircle, XCircle, Loader2 } from 'lucide-react';

// ========================================
// SKELETON COMPONENT
// ========================================
export const ListingsTableSkeleton = () => {
  console.log('🎨 [ListingsTableSkeleton] Rendering skeleton');
  
  return (
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price (KES)</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-5 w-32" /></TableCell>
              <TableCell><Skeleton className="h-5 w-16" /></TableCell>
              <TableCell><Skeleton className="h-5 w-20" /></TableCell>
              <TableCell><Skeleton className="h-5 w-40" /></TableCell>
              <TableCell><Skeleton className="h-6 w-24 rounded-full" /></TableCell>
              <TableCell className="flex gap-2">
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-8 w-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================
export function ListingsTable() {
  console.log('🎨 [ListingsTable] Component mounted');
  
  // ========================================
  // HOOKS & STATE
  // ========================================
  const { toast } = useToast();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalListings, setTotalListings] = useState(0);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    fetchListings();
  }, [currentPage]);

  const fetchListings = async () => {
    console.log('🔵 [ListingsTable] Fetching listings for page:', currentPage);
    setLoading(true);
    
    try {
      const data = await listingsAPI.getListings({ 
        sortBy: 'createdAt', 
        sortOrder: 'desc',
        page: currentPage,
        limit: 12
      });
      console.log('✅ [ListingsTable] Listings received:', data);
      setListings(data.listings || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalListings(data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error('❌ [ListingsTable] Fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load listings');
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not load listings.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  
  const handleUpdateStatus = async (id, status) => {
    console.log('🔵 [ListingsTable] Updating status...', { id, status });
    setUpdatingId(id);
    
    try {
      await listingsAPI.updateListingStatus(id, status);
      console.log('✅ [ListingsTable] Status updated successfully');
      
      // Update local state
      setListings(prevListings =>
        prevListings.map(listing =>
          listing.id === id ? { ...listing, status: status.toUpperCase() } : listing
        )
      );
      
      toast({
        title: 'Success',
        description: `Listing has been ${status}.`,
      });
    } catch (err) {
      console.error('❌ [ListingsTable] Update error:', err);
      
      // ⭐ NEW: Handle tier limit exceeded error
      if (err.response?.data?.error === 'TIER_LIMIT_EXCEEDED') {
        const userInfo = err.response.data.userInfo;
        
        toast({
          variant: 'destructive',
          title: 'Cannot Approve - Tier Limit Reached',
          description: (
            <div className="space-y-2">
              <p className="font-semibold">User: {userInfo.email}</p>
              <p>Tier: {userInfo.tier} ({userInfo.limit} listings max)</p>
              <p>Current Usage: {userInfo.used}/{userInfo.limit}</p>
              <p className="text-sm mt-2">
                User must upgrade their tier or delete an existing listing.
              </p>
            </div>
          ),
          duration: 8000, // Show for 8 seconds
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: err.response?.data?.message || 'Could not update listing status.',
        });
      }
    } finally {
      setUpdatingId(null);
    }
  };
  
  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getStatusVariant = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'approved':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatPrice = (price) => {
    return Number(price).toLocaleString('en-KE');
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    console.log('⏳ [ListingsTable] Loading...');
    return <ListingsTableSkeleton />;
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    console.log('🔴 [ListingsTable] Error state:', error);
    return (
      <CardContent>
        <p className="text-destructive">Error loading listings: {error}</p>
      </CardContent>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [ListingsTable] Rendering table with', listings.length, 'listings');

  return (
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">Price (KES)</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listings.map((listing) => {
            const isUpdating = updatingId === listing.id;
            const statusLower = listing.status?.toLowerCase() || 'pending';
            
            return (
              <TableRow 
                key={listing.id} 
                className={isUpdating ? 'opacity-50' : ''}
              >
                <TableCell className="font-medium">{listing.title}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {listing.category?.toLowerCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {formatPrice(listing.price)}
                </TableCell>
                <TableCell>{listing.user?.email || 'N/A'}</TableCell>
                <TableCell>
                  <Badge 
                    variant={getStatusVariant(listing.status)} 
                    className="capitalize"
                  >
                    {statusLower}
                  </Badge>
                </TableCell>
                <TableCell className="flex justify-center items-center gap-2">
                  {isUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Link to={`/listing/${listing.id}`}>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="View Listing"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-green-600 hover:text-green-700"
                        title="Approve"
                        onClick={() => handleUpdateStatus(listing.id, 'APPROVED')}
                        disabled={statusLower === 'approved'}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700"
                        title="Reject"
                        onClick={() => handleUpdateStatus(listing.id, 'REJECTED')}
                        disabled={statusLower === 'rejected'}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {listings.length === 0 && (
        <p className="text-center text-muted-foreground py-8">
          No listings found.
        </p>
      )}

      <div className="mt-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={totalListings}
          itemsPerPage={12}
          showInfo={true}
        />
      </div>
    </CardContent>
  );
}